import { type FC, useState, useCallback, useEffect, type ReactNode, useMemo } from 'react';

import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { Drawer } from '@vakantiesnl/components/src/atoms/Drawer';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { type MicroCopy } from '@vakantiesnl/types';

import { useStyles } from './MobileFilterMenuWrapper.styles';

export type MobileFilterMenuWrapperTypes = {
	microCopies: MicroCopy;
	onClose?: () => void;
	openButtonText: string;
	numberOfActiveFilters?: number;
	children?: ReactNode;
	drawerTitle?: string;
};

export const MobileFilterMenuWrapper: FC<MobileFilterMenuWrapperTypes> = ({
	microCopies,
	drawerTitle,
	openButtonText,
	numberOfActiveFilters,
	onClose,
	children,
}) => {
	const { classes } = useStyles();
	const numberOfActiveFiltersLabel = useMemo(
		() => (numberOfActiveFilters && numberOfActiveFilters > 0 ? `(${numberOfActiveFilters})` : ''),
		[numberOfActiveFilters],
	);

	const buttonText = useMemo(
		() => `${openButtonText} ${numberOfActiveFiltersLabel}`,
		[numberOfActiveFiltersLabel, openButtonText],
	);
	const { isMobile } = useBreakpoints();
	const [mobileMenuState, setMobileMenuState] = useState<boolean>(false);

	const toggleMenu = useCallback(() => {
		setMobileMenuState(!mobileMenuState);
	}, [setMobileMenuState, mobileMenuState]);

	useEffect(() => {
		if (onClose && mobileMenuState) {
			onClose();
		}
	}, [mobileMenuState, onClose]);

	if (!isMobile) return <>{children}</>;

	return (
		<>
			<Button
				variant="secondary"
				title={buttonText}
				onClick={toggleMenu}
				leadingIcon="filter"
				trailingIcon="chevronDown"
				className={classes.mobileButton}
			>
				<span className={classes.mobileButtonText}>{buttonText}</span>
			</Button>
			{mobileMenuState && (
				<Drawer direction="up" title={drawerTitle} hasCloseIcon={true} onDrawerClose={toggleMenu} open>
					<>
						<div>{children}</div>
						<Button variant="primary" className={classes.applyButton} onClick={toggleMenu}>
							{microCopies['common.apply']}
						</Button>
					</>
				</Drawer>
			)}
		</>
	);
};
